import { CreateButton, useTable } from '@refinedev/antd';
import { HelperLine, ToolList, embeddedTableProps } from '../../components/ToolList';
import { Button, Space, Table, Tag, Tooltip, Typography } from 'antd';

import {
  IResourceComponentsProps,
  useGetIdentity,
  useGetLocale,
  useList,
  useTranslate,
} from '@refinedev/core';

import {
  ArrowDownOutlined,
  EditOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { dbCols, UserRole } from '@cc/schema';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import {
  ClientName,
  ShowNamesButton,
  clientFilterText,
  useClientNamesVisibility,
} from '../../components/clientNames';
import { useClientRoster } from '../../hooks/client-options';
import { IUser } from '../../providers';
import { BehaviorExperiment } from '../../schema/be-graphql-generated';
import { colors } from '../../styles/colors';
import { useContext, useEffect, useMemo } from 'react';
import { TherapistActivityReactContext } from '../../App';
import { useActor } from '@xstate/react';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { useTranslation } from 'react-i18next';
import useStyles from './styles.style';
import { unfinishedDraftFilters } from './unfinishedDrafts';
import {
  canEditExperiment,
  dedupeById,
  deriveStatus,
  experimentTitle,
  observationCount,
  rowKeyOf,
  STATUS_TAG_COLOR,
} from './esHelpers';

const { Text, Title } = Typography;

/**
 * E02 · the list's E-era columns. The row itself carries the additive read fields the inline
 * back-projection writes (handoff §4) — `status`, `observations` — so status and the observation
 * count DERIVE ON READ (the board's "## Behavior: observation count + status derived on read"),
 * with no ES read model during coexistence (plan 04 D3).
 *
 * A row with no `experimentId` is one the log does not own: it shows no status at all rather than a
 * wrong one, and its only action stays Edit (the shipped legacy form).
 */
const E02_LIST_FIELDS = [
  'experimentId',
  'status',
  'activatedAt',
  'completedAt',
  'updatedAt',
  // ids only: the count is all the list needs, and an observation is a PII-heavy record
  { observations: ['observationId'] },
];

const showHref = (id: string) =>
  `/tools/behaviorExperiments/show?behaviorExperimentId=${encodeURIComponent(
    id,
  )}`;

/**
 * E57 · EMBEDDED on the client page with the client FIXED (`fixedClientId` — registry decision `per-client-views`):
 * the same list, filtered to that client whatever the header bar's filter says, with no client column; Create opens the
 * create page with `?clientId=` (Q-e57g1-2 A rule 4); the page that hosts it records the RTM page view (rule 2), so
 * the list's own tracking is off. Standalone (no `fixedClientId`) it behaves exactly as before.
 */
export const BehaviorExperimentsList: React.FC<
  IResourceComponentsProps & { fixedClientId?: string }
> = ({ fixedClientId }) => {
  const translate = useTranslate();
  const { i18n } = useTranslation();
  const { styles } = useStyles();

  const therapistActivityService = useContext(TherapistActivityReactContext);

  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );
  // E57 — the client page fixes the client; otherwise the header bar's filter
  const clientFilterId = fixedClientId || therapistActivityState?.context?.filterClientId;
  const navigateTo = useNavigate();

  const getLocale = useGetLocale();

  const currentLocale = useMemo(() => getLocale(), [i18n.languages[0]]);

  const {
    data: identity,
    remove,
    refetch: refetchIdentity,
  } = useGetIdentity<IUser>();

  // @ts-ignore
  const { tableProps, sorters, searchFormProps, filters, setFilters } =
    useTable<BehaviorExperiment>({
      // E57: the resource is named, never inferred from the route (embedded on the client page the route's is `clients`)
      resource: 'behaviorExperiments',
      queryOptions: {
        select: (data) => {
          // The L12 finding, and the founder saw it again on E02 (QA 2026-09-02): every experiment
          // rendered TWICE. `getPortalDocuments`' practice branch collects `e._to` for EVERY
          // matching therapistClientRelations edge, so a client reachable through two edges (own +
          // admin-view) yields the same document once per edge. The store holds one row; the table
          // de-dupes by `_id` before it renders, exactly as supportingCycles/list.tsx does.
          // @ts-ignore
          return { data: dedupeById(data.data.data), total: data.total };
        },
      },

      meta: {
        operation: 'behaviorExperiments',
        fields: [
          {
            data: [
              '_id',
              'userId',
              'createdAt',
              'updatedAt',
              'hypothesisA',
              'hypothesisB',
              {
                // `newThought` is a COLUMN; the rest are the run-like leaves the observation count
                // needs, because a CONVERTED row's single legacy outcome IS its first observation
                // (founder ruling 2026-09-02). The reflection leaves are no longer read here — the
                // status of a converted row is `active` regardless of them — so the list carries
                // less of the client's writing than it did.
                outcome: [
                  'newThought',
                  'behaviors',
                  'otherPredictions',
                  'newLearnings',
                  { emotions: ['id'] },
                  'feelings',
                ],
              },
              'followUpExperimentsIdeas',
              { settings: ['experiment'] },
              ...E02_LIST_FIELDS,
            ],
          },
          'count',
        ],
      },

      filters: {
        initial: clientFilterId
          ? [
              {
                field: 'userId',
                operator: 'eq',
                value: clientFilterId,
              },
            ]
          : [],
      },

      sorters: {
        initial: [
          {
            field: 'updatedAt',
            order: 'desc',
          },
        ],
      },
    });

  useEffect(() => {
    setFilters(
      clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
      'replace',
    );
  }, [clientFilterId]);

  /**
   * The roster this page reads to label rows, fill the client filter and answer the EDIT GATE — so
   * it has to cover every client whose experiment the table can show. It is read WHOLE, once, by
   * the shared hook (P011): the page-local `clientsPurchased * 4` read this page used to do
   * stopped at the 20th client of the demo practice, and every row below that answered "client
   * unknown" → no pencil at all (founder QA 2026-09-02, defect A).
   */
  // Q-mt-3b A: "id · initials", full name on hover; Show names for 60 s
  const names = useClientNamesVisibility();
  const { clients } = useClientRoster();

  useActivityTracking(dbCols.behaviorExperiments, fixedClientId, !fixedClientId);

  /**
   * E02 · Unfinished designs (board read model UnfinishedBehaviorExperiments, portal) — the
   * experiments a CLIENT started in the app and never activated, so the therapist can finish one
   * together in session.
   *
   * The filter is SERVER-SIDE (`status_eq: 'draft'`, founder review 2026-09-02): the therapist
   * branch of `behaviorExperiments` now passes the whole `where` through
   * (resolvers/behaviorExperiment/index.ts), so this reads exactly the drafts instead of pulling a
   * bounded 100-row window and filtering in the browser — which silently missed any draft older
   * than that window. Only a NATIVE draft carries `status: 'draft'`; a converted row carries no
   * status at all and is never a draft, so the server-side filter says exactly what the derivation
   * used to say, and says it about every page.
   */
  const { data: unfinishedData } = useList<{
    data: BehaviorExperiment[];
    count: number;
  }>({
    resource: 'behaviorExperiments',
    meta: {
      fields: [
        {
          data: [
            '_id',
            'userId',
            'createdAt',
            'updatedAt',
            'hypothesisA',
            'hypothesisB',
            { settings: ['experiment'] },
            ...E02_LIST_FIELDS,
          ],
        },
        'count',
      ],
    },
    // the chosen client's drafts only (E57 review F5) — on her page the card never lists another client's
    filters: unfinishedDraftFilters(clientFilterId),
    sorters: [{ field: 'updatedAt', order: 'desc' }],
  });

  // …and de-duped the same way as the table below: this read goes through the SAME practice branch,
  // so a client reachable through two permission edges would list their draft twice too.
  const unfinished = useMemo(
    () =>
      dedupeById(
        (unfinishedData?.data as unknown as { data?: BehaviorExperiment[] })
          ?.data,
      ),
    [unfinishedData],
  );

  const clientLabel = (userId?: string | null) => {
    const c = clients.find((x) => x._id === userId);
    return (
      <ClientName
        id={userId}
        firstName={c?.firstName}
        lastName={c?.lastName}
        visible={names.visible}
      />
    );
  };

  const statusTag = (record: BehaviorExperiment) => {
    const status = deriveStatus(record);
    if (!status) return <Text type="secondary">—</Text>;
    return (
      <Tag
        color={STATUS_TAG_COLOR[status]}
        data-testid={`cc-behavior-experiment-status-${rowKeyOf(record._id)}`}
        data-status={status}
      >
        {translate(`pages.behaviorExperiment.statuses.${status}`)}
      </Tag>
    );
  };

  return (
    <ToolList
      fixedClientId={fixedClientId}
      tableProps={tableProps}
      help={translate('pages.behaviorExperiment.unfinishedHint')}
      breadcrumb={fixedClientId ? false : undefined}
      title={translate('shared.behaviorExperiments.title')}
      headerButtons={
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          {fixedClientId ? null : (
            <ShowNamesButton
              visible={names.visible}
              onToggle={names.toggle}
              testId="cc-behavior-experiments-toggle-names"
            />
          )}
          <CreateButton
            data-testid="create-behavior-experiment-button"
            onClick={
              fixedClientId
                ? () => navigateTo(`/tools/behaviorExperiments/create?clientId=${encodeURIComponent(fixedClientId)}`)
                : undefined
            }
          />
        </div>
      }
    >
      {/* E57 · Q-nav-6 A: not drawn when nothing is unfinished; otherwise one line, Show opens the list */}
      {unfinished.length === 0 ? null : (
        <HelperLine
          title={translate('pages.behaviorExperiment.unfinished')}
          count={unfinished.length}
          testId="cc-behavior-experiment-unfinished"
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {unfinished.map((row) => (
              <Space
                key={row._id}
                wrap
                data-testid={`cc-behavior-experiment-unfinished-${rowKeyOf(
                  row._id,
                )}`}
              >
                <Text strong>{clientLabel(row.userId)}</Text>
                <Text>{experimentTitle(row) || '—'}</Text>
                <Text type="secondary">
                  {translate('pages.behaviorExperiment.started')}:{' '}
                  {row.createdAt
                    ? format(new Date(row.createdAt), 'yyyy-MM-dd')
                    : '—'}
                </Text>
                <Link
                  to={`/tools/behaviorExperiments/create?behaviorExperimentId=${row._id}`}
                >
                  <Button size="small" icon={<EditOutlined />}>
                    {translate('pages.behaviorExperiment.resume')}
                  </Button>
                </Link>
              </Space>
            ))}
          </Space>
        </HelperLine>
      )}

      <Table
        {...embeddedTableProps(fixedClientId)}
        {...tableProps}
        rowKey="_id"
        onChange={(pagination, filters, sorter, extra) => {
          // Note: The data we get from Ant.design filters does not match the
          // react refine one. Currently it works fine if I simply silence it the way below.
          // TOOD: Find a way to convert the data to match the react refine one and fix it better
          // @ts-ignore
          tableProps?.onChange?.(pagination, null, sorter, extra);
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
        }}
        data-testid="behavior-experiments-list"
      >
        {fixedClientId ? null : (
        <Table.Column
          key="_id"
          dataIndex="userId"
          title={translate('resourcesCommon.userId')}
          sorter
          filterSearch
          // @ts-ignore
          onFilter={(value: string, record: DownwardArrow) => {
            return record.userId === value;
          }}
          filters={clients.map((c) => {
            return {
              text: clientFilterText(c, names.visible),
              value: c._id,
            };
          })}
          render={(value, record) => {
            const client = clients.find((c) => c._id === value);

            return (
              <ClientName
                id={value}
                firstName={client?.firstName}
                lastName={client?.lastName}
                visible={names.visible}
              />
            );
          }}
        />
        )}

        <Table.Column<BehaviorExperiment>
          key="beleifs"
          dataIndex="beleifs"
          title={translate('shared.behaviorExperiments.initialThoughtOrBelief')}
          render={(text, record, indx) => {
            return record?.hypothesisA || record?.hypothesisB || '';
          }}
        />

        <Table.Column<BehaviorExperiment>
          key="beleifs"
          dataIndex="beleifs"
          title={translate('shared.behaviorExperiment.newThought')}
          render={(text, record, indx) => {
            return record?.outcome?.newThought || '';
          }}
        />

        <Table.Column<BehaviorExperiment>
          key="beleifs"
          dataIndex="beleifs"
          title={translate(
            'shared.behaviorExperiment.followUpExperimentsIdeas',
          )}
          render={(text, record, indx) => {
            return record?.followUpExperimentsIdeas || '';
          }}
        />

        {/* ── E02: status + observation count, both DERIVED ON READ (board ## Behavior) ── */}
        <Table.Column<BehaviorExperiment>
          key="status"
          dataIndex="status"
          title={translate('pages.behaviorExperiment.status')}
          render={(_text, record) => statusTag(record)}
        />

        <Table.Column<BehaviorExperiment>
          key="observations"
          dataIndex="observations"
          title={translate('pages.behaviorExperiment.observations')}
          render={(_text, record) =>
            record.experimentId ? (
              <span
                data-testid={`cc-behavior-experiment-observations-${rowKeyOf(
                  record._id,
                )}`}
              >
                {observationCount(record)}
              </span>
            ) : (
              <Text type="secondary">—</Text>
            )
          }
        />

        <Table.Column
          key="updatedAt"
          dataIndex="updatedAt"
          title={translate('resourcesCommon.updatedAt')}
          sorter
          render={(text, record, indx) => {
            if (!text) return undefined;
            const date = new Date(text);
            return format(
              date,
              currentLocale?.startsWith('ru')
                ? 'yyyy-MM-dd HH:mm'
                : 'yyyy-MM-dd hh:mm aaa',
            );
          }}
        />

        <Table.Column<BehaviorExperiment>
          title={translate('resourcesCommon.actions')}
          dataIndex="actions"
          render={(_, record) => {
            const client = clients.find((c) => c._id === record.userId);

            /**
             * THE edit gate — one predicate, shared with the experiment view's "Edit design" header
             * button (`canEditExperiment`, esHelpers). The rule itself is the shipped L02 one and is
             * unchanged: an edit relation offers the pencil, the client's paid account enables it.
             */
            const editGate = canEditExperiment(client, {
              isTherapistAdmin: identity?.db?.role === UserRole.THERAPIST_ADMIN,
            });

            return (
              <Space>
                {/* E02: the experiment view (design + observations + completion) exists only for a
                    row the LOG owns — a legacy row has no observations to show (handoff §4) */}
                {record.experimentId ? (
                  <Tooltip title={translate('pages.behaviorExperiment.view')}>
                    <Link to={showHref(record._id)}>
                      <Button
                        icon={<EyeOutlined />}
                        data-testid={`cc-behavior-experiment-view-${rowKeyOf(
                          record._id,
                        )}`}
                      />
                    </Link>
                  </Tooltip>
                ) : null}
                {editGate.offered && (
                  <Tooltip title={translate(editGate.tooltipKey)}>
                    <Link
                      to={`/tools/behaviorExperiments/create?behaviorExperimentId=${record._id}`}
                    >
                      <Button
                        icon={<EditOutlined />}
                        disabled={!editGate.enabled}
                        data-testid={`cc-behavior-experiment-edit-${rowKeyOf(
                          record._id,
                        )}`}
                      />
                    </Link>
                  </Tooltip>
                )}
              </Space>
            );
          }}
        />
      </Table>
    </ToolList>
  );
};
